
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Paperclip, Download, Eye, File, AlertTriangle } from "lucide-react";
import { fetchAttachment, openAttachment, downloadAttachment } from "@/utils/gmail/attachmentUtils";
import { EmailAttachment } from "@/types/email";
import { useToast } from "@/components/ui/use-toast";
import { getValidToken } from "@/utils/gmail/tokenUtils";
import { supabase } from "@/lib/supabase";

interface AttachmentViewerProps {
  attachments: EmailAttachment[];
}

const AttachmentViewer = ({ attachments }: AttachmentViewerProps) => {
  const [loading, setLoading] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState<'view' | 'download' | null>(null);
  const [hasValidToken, setHasValidToken] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch current user's primary email account
  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          console.error("No authenticated user found");
          return;
        }

        const { data: userEmailAccounts, error } = await supabase
          .from('email_accounts')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: true })
          .limit(1);

        if (error) {
          console.error("Error fetching user email account:", error);
          return;
        }

        if (userEmailAccounts && userEmailAccounts.length > 0) {
          const email = userEmailAccounts[0].email;
          setUserEmail(email);
          console.log("Using user email account for attachments:", email);
          
          // Check token validity for this email
          const token = getValidToken(email);
          setHasValidToken(!!token);
        }
      } catch (error) {
        console.error("Error in fetchUserEmail:", error);
      }
    };

    fetchUserEmail();
  }, []);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const handleViewAttachment = async (attachment: EmailAttachment) => {
    try {
      // Early check for token validity
      if (!hasValidToken || !userEmail) {
        toast({
          title: "Authentication Error",
          description: "Your Gmail session has expired or no email account is connected. Please check your settings.",
          variant: "destructive",
        });
        return;
      }

      setLoading(attachment.id);
      setLoadingAction('view');
      console.log(`Starting view operation for attachment: ${attachment.filename} (${attachment.id})`);
      console.log(`Using user email: ${userEmail}`);
      
      const base64Data = await fetchAttachment(attachment.messageId, attachment.id, userEmail);
      console.log(`Fetched attachment data, now opening: ${attachment.filename}, data length: ${base64Data.length}`);
      
      try {
        const success = openAttachment(attachment, base64Data);
        
        if (success) {
          toast({
            title: "Attachment Opened",
            description: `${attachment.filename} has been opened in a new tab.`,
          });
        } else {
          throw new Error("Failed to open attachment");
        }
      } catch (openError) {
        console.error("Error in openAttachment:", openError);
        toast({
          title: "Error Opening File",
          description: "Could not open the attachment. It may be in an unsupported format or pop-ups might be blocked.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error viewing attachment:", error);
      
      if (error.message?.includes("No valid Gmail token")) {
        toast({
          title: "Authentication Error",
          description: "Your Gmail session has expired. Please reconnect your account in settings.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: `Failed to open attachment: ${error.message}. Please try again.`,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(null);
      setLoadingAction(null);
    }
  };

  const handleDownloadAttachment = async (attachment: EmailAttachment) => {
    try {
      // Early check for token validity
      if (!hasValidToken || !userEmail) {
        toast({
          title: "Authentication Error",
          description: "Your Gmail session has expired or no email account is connected. Please check your settings.",
          variant: "destructive",
        });
        return;
      }

      setLoading(attachment.id);
      setLoadingAction('download');
      console.log(`Starting download operation for: ${attachment.filename} (${attachment.id})`);
      console.log(`Using user email for download: ${userEmail}`);
      
      const base64Data = await fetchAttachment(attachment.messageId, attachment.id, userEmail);
      console.log(`Fetched attachment data for download: ${attachment.filename}, data length: ${base64Data.length}`);
      
      try {
        const success = downloadAttachment(attachment, base64Data);
        
        if (success) {
          toast({
            title: "Download Started",
            description: `Downloading ${attachment.filename}`,
          });
        } else {
          throw new Error("Download failed in the browser");
        }
      } catch (downloadError) {
        console.error("Error in downloadAttachment:", downloadError);
        toast({
          title: "Download Failed",
          description: "Browser could not download the attachment. Please check your browser settings.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error downloading attachment:", error);
      
      if (error.message?.includes("No valid Gmail token")) {
        toast({
          title: "Authentication Error",
          description: "Your Gmail session has expired. Please reconnect your account in settings.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Download Failed",
          description: `Could not download the attachment: ${error.message}. Please try again.`,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(null);
      setLoadingAction(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <Eye className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  const canViewInBrowser = (mimeType: string) => {
    return mimeType.startsWith('image/') || 
           mimeType === 'application/pdf' || 
           mimeType.startsWith('text/');
  };

  return (
    <div className="mb-4 bg-gray-50 p-3 rounded-md border">
      <div className="flex items-center mb-2">
        <Paperclip className="h-4 w-4 mr-2" />
        <span className="font-medium">Attachments ({attachments.length})</span>
      </div>
      
      {!hasValidToken && (
        <div className="bg-amber-50 text-amber-700 p-2 mb-3 rounded-md flex items-center text-sm border border-amber-200">
          <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
          <span>Gmail session expired or no email account connected. Please check your settings to access attachments.</span>
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-2">
        {attachments.map((attachment) => (
          <div 
            key={attachment.id} 
            className="flex items-center justify-between p-2 rounded border bg-white hover:bg-gray-50"
          >
            <div className="flex items-center space-x-2 overflow-hidden">
              {getFileIcon(attachment.mimeType)}
              <div className="overflow-hidden overflow-ellipsis whitespace-nowrap">
                <div className="font-medium text-sm overflow-hidden overflow-ellipsis" title={attachment.filename}>
                  {attachment.filename}
                </div>
                <div className="text-xs text-gray-500">{formatFileSize(attachment.size)}</div>
              </div>
            </div>
            <div className="flex space-x-2">
              {canViewInBrowser(attachment.mimeType) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleViewAttachment(attachment)}
                  disabled={loading === attachment.id || !hasValidToken}
                >
                  {loading === attachment.id && loadingAction === 'view' ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-1" />
                  ) : (
                    <Eye className="h-4 w-4 mr-1" />
                  )}
                  View
                </Button>
              )}
              <Button
                size="sm"
                variant={canViewInBrowser(attachment.mimeType) ? "ghost" : "outline"}
                onClick={() => handleDownloadAttachment(attachment)}
                disabled={loading === attachment.id || !hasValidToken}
              >
                {loading === attachment.id && loadingAction === 'download' ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-1" />
                ) : (
                  <Download className="h-4 w-4 mr-1" />
                )}
                Download
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AttachmentViewer;
